import { toUrlT } from '@execonline-inc/url';
import { assertNever } from '@kofno/piper';
import { action, makeObservable, observable } from 'mobx';
import { Task } from 'taskarian';
import { callApi } from '../../../../../../Appy';
import { currentUserStore } from '../../../../../../CurrentUserStore';
import { findLinkT } from '../../../../../../LinkyLinky';
import { Link } from '../../../../../../Resource/Types';
import { licenseAlertsResponseDecoder } from './Decoders';
import {
  error,
  LicenseAlertsError,
  LicenseAlertsResource,
  LicenseAlertsState,
  loading,
  ready,
  Request,
  waiting,
} from './Types';

const sameIds = (left: ReadonlyArray<number>, right: ReadonlyArray<number>): boolean =>
  left.length === right.length && left.every((id, index) => id === right[index]);

const sameEmails = (left: ReadonlyArray<string>, right: ReadonlyArray<string>): boolean =>
  left.length === right.length && left.every((email, index) => email === right[index]);

const sameRequest = (left: Request, right: Request): boolean =>
  left.learningCollectionId === right.learningCollectionId &&
  sameIds(left.userIds, right.userIds) &&
  sameIds(left.groupIds, right.groupIds) &&
  sameEmails(left.notFoundEmails, right.notFoundEmails);

export class LicenseAlertsStore {
  @observable state: LicenseAlertsState = waiting();

  constructor() {
    makeObservable(this);
  }

  // True when what is loaded (or loading) already answers this request, so the
  // banner's background refresh and the Confirm click do not fire the same
  // lookup twice.
  covers = (request: Request): boolean => {
    switch (this.state.kind) {
      case 'waiting':
        return false;
      case 'loading':
      case 'ready':
      case 'error':
        return sameRequest(this.state.request, request);
      default:
        return assertNever(this.state);
    }
  };

  @action
  load = (request: Request): void => {
    this.state = loading(request);

    this.loadLicenseAlertsTask(request).fork(
      action((err: LicenseAlertsError) => {
        console.error('Failed to load license alerts:', err);
        this.state = error(request, 'An error occurred, please refresh the page and try again');
      }),
      action((licenseAlertsResource: LicenseAlertsResource) => {
        this.state = ready(request, licenseAlertsResource);
      }),
    );
  };

  @action
  refresh = (): void => {
    switch (this.state.kind) {
      case 'waiting':
        break;
      case 'loading':
        break;
      case 'ready':
      case 'error':
        this.load(this.state.request);
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  reset = (): void => {
    this.state = waiting();
  };

  private loadLicenseAlertsTask = (
    request: Request,
  ): Task<LicenseAlertsError, LicenseAlertsResource> => {
    return Task.succeed<LicenseAlertsError, ReadonlyArray<Link>>(currentUserStore.links)
      .andThen(findLinkT('adminstudio-share-learning-collections-license-alerts'))
      .andThen(this.addLearningCollectionIdToPath(request.learningCollectionId))
      .andThen(this.addParamsToUrl(request))
      .andThen(callApi(licenseAlertsResponseDecoder, {}));
  };

  private addLearningCollectionIdToPath =
    (learningCollectionId: number) =>
    (link: Link): Task<LicenseAlertsError, Link> => {
      return toUrlT(link.href).map((u) => {
        const newHref = u.href.replace('{learning_collection_id}', learningCollectionId.toString());
        return { ...link, href: newHref };
      });
    };

  private addParamsToUrl =
    (request: Request) =>
    (link: Link): Task<LicenseAlertsError, Link> => {
      return toUrlT(link.href).map((u) => {
        const params = new URLSearchParams();

        request.userIds.forEach((id) => {
          params.append('user_ids[]', id.toString());
        });

        request.groupIds.forEach((id) => {
          params.append('group_ids[]', id.toString());
        });

        request.notFoundEmails.forEach((email) => {
          params.append('not_found[]', email);
        });

        const queryString = params.toString();
        const separator = u.href.includes('?') ? '&' : '?';
        const newHref = queryString ? `${u.href}${separator}${queryString}` : u.href;

        return { ...link, href: newHref };
      });
    };
}

const licenseAlertsStore = new LicenseAlertsStore();

export { licenseAlertsStore };
